/**
 * 3D Room Visualizer — SlatWall accessories, from Trusscore's STL files.
 *
 * Each kind's geometry is fetched once (public/models/slatwall) and kept:
 * millimetres become feet, and the file's frame (x out from the wall, y up,
 * z along the wall) is turned into the canonical wall frame (x along the
 * wall, y up, z out into the room), with the model's bottom-left-back corner
 * at the origin — so a mesh is placed by its bottom-left on the wall, like
 * everything else on a surface. Black steel, as the real ones are.
 *
 * Loading is asynchronous: `placeAccessory` adds the mesh to its wall when
 * the geometry arrives (unless the wall has been taken down by then) and asks
 * the caller to draw again.
 */

import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { ACCESSORY_MODEL_PATH, SlatWallAccessoryKind, accessoryDef } from '../../../data/slatwallAccessories';
import { applyFaceTransformToGroup } from './extrudedBoardBuilder';
import { WallFace } from './types';

const MM = 1 / 304.8;

const geometries = new Map<SlatWallAccessoryKind, Promise<THREE.BufferGeometry>>();

/** The kind's geometry in the canonical wall frame, feet, corner at the origin (fetched once). */
export function loadAccessoryGeometry(kind: SlatWallAccessoryKind): Promise<THREE.BufferGeometry> {
  let pending = geometries.get(kind);
  if (!pending) {
    const def = accessoryDef(kind);
    pending = new STLLoader().loadAsync(ACCESSORY_MODEL_PATH + def.model).then((g) => {
      g.scale(MM, MM, MM);
      // File x (out from the wall) → canonical +z; file z (along the wall) → canonical x.
      // A quarter turn about y does it as a rotation (no mirrored winding). A
      // file modelled with its wall side at x = max turns the other way.
      g.rotateY(def.flipDepth ? Math.PI / 2 : -Math.PI / 2);
      g.computeBoundingBox();
      const min = g.boundingBox!.min;
      g.translate(-min.x, -min.y, -min.z);
      g.computeVertexNormals();
      g.computeBoundingBox();
      return g;
    });
    geometries.set(kind, pending);
  }
  return pending;
}

const STEEL = new THREE.MeshStandardMaterial({ color: 0x1b1b1e, roughness: 0.55, metalness: 0.35 });

/** Is the object still part of a scene (its room has not been rebuilt under it)? */
function inScene(object: THREE.Object3D): boolean {
  let o: THREE.Object3D | null = object;
  while (o) {
    if ((o as THREE.Scene).isScene) return true;
    o = o.parent;
  }
  return false;
}

export interface AccessoryPlacement {
  /** For the viewer: the user's accessory id (kept visible in edit mode) or the staged prop's id (hidden then). */
  id: string;
  staged: boolean;
  kind: SlatWallAccessoryKind;
  face: WallFace;
  /** Bottom-left on the wall, surface coordinates (feet). */
  x: number;
  y: number;
  /** How far the wall's product stands proud of the wall plane (feet): the accessory sits against it. */
  z: number;
}

/**
 * Put an accessory on its wall once its geometry is there. The mesh is built
 * in canonical wall coordinates and given the wall's face transform, like a
 * hung prop. Returns the group (empty until loaded).
 */
export function placeAccessory(
  wall: THREE.Object3D, p: AccessoryPlacement, roomLength: number, roomWidth: number, onReady?: () => void,
): THREE.Group {
  const group = new THREE.Group();
  group.name = `accessory-${p.kind}`;
  if (p.staged) group.userData.stagedPropId = p.id;
  else group.userData.accessoryId = p.id;
  wall.add(group);
  void loadAccessoryGeometry(p.kind).then((shared) => {
    if (!inScene(group)) return; // the room was rebuilt while the file was on its way
    // Each placement gets its own copy: the face transform for the left and
    // right walls rewrites vertices, and the position is baked in the same way.
    const geometry = shared.clone();
    // Pushed into the wall by the depth of its tabs: the plate sits on the
    // face of the slats and the tabs are inside the groove channels.
    geometry.translate(p.x, p.y, p.z - accessoryDef(p.kind).seat.tabDepth);
    // Its own material: the viewer fades a wall by writing opacity onto each of
    // its meshes' materials, and a material shared across walls would carry the
    // faded walls' zero to the wall being edited.
    const mesh = new THREE.Mesh(geometry, STEEL.clone());
    mesh.castShadow = true;
    mesh.receiveShadow = false;
    const holder = new THREE.Group();
    holder.add(mesh);
    applyFaceTransformToGroup(holder, p.face, roomLength, roomWidth);
    group.add(holder);
    onReady?.();
  }).catch(() => undefined);
  return group;
}

/** Fetch every kind's geometry ahead of need (the palette opening, say). */
export function preloadAccessories(kinds: SlatWallAccessoryKind[]): void {
  for (const kind of kinds) void loadAccessoryGeometry(kind).catch(() => undefined);
}
